import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Breadcrumbs,
  Link,
  CircularProgress,
  Alert,
  Paper
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import {
  NavigateNext as NavigateNextIcon,
  Home as HomeIcon
} from '@mui/icons-material';
import { getPageBySlug } from '../../services/pageService';
import { Page } from '../../types/page';

const StaticPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [page, setPage] = useState<Page | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // تحميل بيانات الصفحة
  useEffect(() => {
    const loadPage = async () => {
      try {
        setLoading(true);
        setError(null);

        if (!slug) {
          console.error('Page slug is missing');
          setError('معرف الصفحة غير موجود');
          setLoading(false);
          return;
        }

        console.log('Loading page data for slug:', slug);

        const pageData = await getPageBySlug(slug);
        if (!pageData) {
          console.error('Page not found for slug:', slug);
          setError('الصفحة غير موجودة');
          setLoading(false);
          return;
        }

        // التحقق من أن الصفحة نشطة
        if (!pageData.isActive) {
          console.error('Page is not active:', slug);
          setError('الصفحة غير متاحة حاليًا');
          setLoading(false);
          return;
        }

        setPage(pageData);
        console.log('Page data loaded:', pageData);
        setLoading(false);
      } catch (err) {
        console.error('Error loading page data:', err);
        setError('حدث خطأ أثناء تحميل بيانات الصفحة');
        setLoading(false);
      }
    };

    loadPage();
  }, [slug]);

  // تعيين عنوان الصفحة
  useEffect(() => {
    if (page) {
      document.title = page.metaTitle || page.title;

      // إضافة وصف الصفحة إذا كان موجودًا
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription && page.metaDescription) {
        metaDescription.setAttribute('content', page.metaDescription);
      }
    }
  }, [page]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!page) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="warning">الصفحة غير موجودة</Alert>
      </Container>
    );
  }

  return (
    <>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* فتات الخبز */}
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="breadcrumb"
          sx={{ mb: 3 }}
        >
          <Link component={RouterLink} to="/" color="inherit" sx={{ display: 'flex', alignItems: 'center' }}>
            <HomeIcon sx={{ mr: 0.5, fontSize: 20 }} />
            الرئيسية
          </Link>
          <Typography color="text.primary">{page.title}</Typography>
        </Breadcrumbs>

        <Paper elevation={2} sx={{ p: { xs: 2, sm: 3, md: 4 }, borderRadius: 2 }}>
          <Box className="static-page-content" sx={{ '& img': { maxWidth: '100%', height: 'auto' } }}>
            <div dangerouslySetInnerHTML={{ __html: page.content }} />
          </Box>
        </Paper>
      </Container>
    </>
  );
};

export default StaticPage;
